import React, { useState } from "react";
import { Form } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Stack } from "@mui/material";
import classes from "./AuthForm.module.scss";
import InputField from "../../../components/ui/Input/InputField";
import Button from "../../../components/ui/button/Button";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import {
    CognitoIdentityProviderClient,
    ForgotPasswordCommand,
    ConfirmForgotPasswordCommand,
} from "@aws-sdk/client-cognito-identity-provider";

interface ForgotPasswordProps {
    onClickSignup: (form: string) => void;
    onClickLogin: (form: string) => void;
}

const cognitoClient = new CognitoIdentityProviderClient({
    region: "ca-central-1", // Replace with your AWS Cognito region
});

const ForgotPassword = ({ onClickSignup, onClickLogin }: ForgotPasswordProps) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        email: "",
        verificationCode: "",
        password: "",
        confirmPassword: "",
    });

    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isPasswordChanged, setIsPasswordChanged] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError(false);
        setErrorMessage("");
        setSuccessMessage("");
    };

    const handleSendResetCode = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (!formData.email) {
            setError(true);
            setErrorMessage("Email is required.");
            return;
        }

        try {
            const command = new ForgotPasswordCommand({
                ClientId: "5u806hjmmcee0qu442qddug8gv", // Replace with your Cognito App Client ID
                Username: formData.email,
            });

            await cognitoClient.send(command);
            setSuccessMessage("A verification code has been sent to your email.");
            setIsCodeSent(true);
            setError(false);
        } catch (error) {
            console.error("Forgot password error:", error);
            setError(true);
            setErrorMessage(
                "Failed to send verification code. Please check your email and try again.",
            );
        }
    };

    const handleChangePassword = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (!formData.verificationCode || !formData.password || !formData.confirmPassword) {
            setError(true);
            setErrorMessage("All fields are required.");
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError(true);
            setErrorMessage("Passwords do not match.");
            return;
        }

        try {
            const command = new ConfirmForgotPasswordCommand({
                ClientId: "5u806hjmmcee0qu442qddug8gv", // Replace with your Cognito App Client ID
                Username: formData.email,
                ConfirmationCode: formData.verificationCode,
                Password: formData.password,
            });

            await cognitoClient.send(command);
            setIsPasswordChanged(true);
            setError(false);
            onClickLogin("login");
        } catch (error) {
            console.error("Change password error:", error);
            setError(true);
            setErrorMessage("Failed to change password. Please check the code and try again.");
        }
    };

    return (
        <Stack spacing={2} className={classes.authFormContainer}>
            <Box>
                <Form className={classes.authForm}>
                    <div className={classes.welcomeText}>
                        <h4>Forgot Password?</h4>
                        <p>Enter your email to receive a password reset code.</p>
                    </div>

                    {/* Email Input */}
                    <InputField
                        name="email"
                        label="Email"
                        type="email"
                        placeholder="Enter email address"
                        value={formData.email}
                        error={error}
                        helperText={error ? errorMessage : successMessage}
                        onChange={handleInputChange}
                    />

                    {/* Button: Send Reset Code */}
                    {!isCodeSent && (
                        <Button
                            label="Send Reset Code"
                            size="small"
                            type="submit"
                            variant="primary"
                            onClick={handleSendResetCode}
                        />
                    )}

                    {/* Verification Code Input (Only shown after code is sent) */}
                    {isCodeSent && (
                        <InputField
                            name="verificationCode"
                            label="Verification Code"
                            type="text"
                            placeholder="Enter verification code"
                            value={formData.verificationCode}
                            error={error}
                            helperText="Check your email for the code."
                            onChange={handleInputChange}
                        />
                    )}

                    {/* New Password & Confirm Password Inputs (Only shown after code is sent) */}
                    {isCodeSent && (
                        <>
                            <InputField
                                name="password"
                                label="New Password"
                                placeholder="Enter new password"
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                error={error}
                                helperText={errorMessage}
                                onChange={handleInputChange}
                                icon={
                                    showPassword ? (
                                        <VisibilitySharpIcon
                                            onClick={() => setShowPassword(!showPassword)}
                                        />
                                    ) : (
                                        <VisibilityOffIcon
                                            onClick={() => setShowPassword(!showPassword)}
                                        />
                                    )
                                }
                            />

                            <InputField
                                name="confirmPassword"
                                label="Confirm Password"
                                placeholder="Re-enter new password"
                                type={showConfirmPassword ? "text" : "password"}
                                value={formData.confirmPassword}
                                error={error}
                                helperText={errorMessage}
                                onChange={handleInputChange}
                                icon={
                                    showConfirmPassword ? (
                                        <VisibilitySharpIcon
                                            onClick={() =>
                                                setShowConfirmPassword(!showConfirmPassword)
                                            }
                                        />
                                    ) : (
                                        <VisibilityOffIcon
                                            onClick={() =>
                                                setShowConfirmPassword(!showConfirmPassword)
                                            }
                                        />
                                    )
                                }
                            />
                        </>
                    )}

                    {/* Button: Change Password */}
                    {isCodeSent && (
                        <Button
                            label="Change Password"
                            size="small"
                            type="submit"
                            variant="primary"
                            onClick={handleChangePassword}
                        />
                    )}

                    {isPasswordChanged && (
                        <div className={classes.successMessage}>
                            <p>
                                Password changed successfully!{" "}
                                <span onClick={() => onClickLogin("login")}>Login now</span>
                            </p>
                        </div>
                    )}

                    <div className={classes.alternativeAuthLink}>
                        <p>
                            Remember your password?{" "}
                            <span
                                onClick={() => onClickLogin("login")}
                                className={classes.underlineText}
                            >
                                Login
                            </span>
                        </p>
                        <p>
                            Don&apos;t have an account?{" "}
                            <span
                                onClick={() => onClickSignup("signup")}
                                className={classes.underlineText}
                            >
                                Sign Up
                            </span>
                        </p>
                    </div>
                </Form>
            </Box>
        </Stack>
    );
};

export default ForgotPassword;
